import type { EvArtifactFreshnessState, EvCallReadyStatus } from "../types/index.ts";

export type CallPrepVisualStatus =
  | "idle"
  | "generating"
  | "fresh"
  | "stale"
  | "needs_input"
  | "failed";

interface ToCallPrepVisualStatusInput {
  callReadyStatus: EvCallReadyStatus | string;
  artifactStatus?: string | null;
  artifactPayload?: unknown;
  artifactFreshnessState?: EvArtifactFreshnessState | null;
  liveStatus?: string | null;
  liveFreshnessState?: EvArtifactFreshnessState | null;
}

export function toCallPrepVisualStatus(input: ToCallPrepVisualStatusInput): CallPrepVisualStatus {
  const {
    callReadyStatus,
    artifactStatus,
    artifactPayload,
    artifactFreshnessState,
    liveStatus,
    liveFreshnessState,
  } = input;

  const effectiveStatus = liveStatus ?? artifactStatus;
  const effectiveFreshness = liveFreshnessState ?? artifactFreshnessState;
  const hasPayload = artifactPayload !== null && artifactPayload !== undefined;
  const hasStalePayload =
    hasPayload && (effectiveFreshness === "stale" || effectiveFreshness === "invalidated");

  if (effectiveStatus === "failed") {
    return "failed";
  }

  if (
    effectiveStatus === "queued" ||
    effectiveStatus === "retrieving" ||
    effectiveStatus === "drafting" ||
    effectiveStatus === "validating" ||
    callReadyStatus === "requested" ||
    callReadyStatus === "generating"
  ) {
    return "generating";
  }

  if (
    hasStalePayload &&
    (effectiveStatus === "completed" ||
      effectiveStatus === "needs_input" ||
      callReadyStatus === "ready" ||
      callReadyStatus === "needs_input")
  ) {
    return "stale";
  }

  if (effectiveStatus === "needs_input" || callReadyStatus === "needs_input") {
    return "needs_input";
  }

  if (effectiveStatus === "completed" || callReadyStatus === "ready") {
    if (effectiveFreshness === "stale" || effectiveFreshness === "invalidated") {
      return "stale";
    }
    if (artifactPayload) {
      return "fresh";
    }
    return "stale";
  }

  return "idle";
}

export function toWorkflowCallReadyVisualStatus(
  callReadyStatus: EvCallReadyStatus | string
): CallPrepVisualStatus | null {
  switch (callReadyStatus) {
    case "requested":
    case "generating":
      return "generating";
    case "ready":
      return "fresh";
    case "needs_input":
      return "needs_input";
    default:
      return null;
  }
}

const ACTIVE_RUNTIME_STATUSES = new Set(["queued", "retrieving", "drafting", "validating"]);

export function isActiveCallPrepRuntimeStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return ACTIVE_RUNTIME_STATUSES.has(status);
}
